import React, { useEffect, useState, useRef, useCallback } from 'react'
import clsx from 'clsx'
import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix/fetcher'
import ReservedTickets from './reserved-tickets'
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline'
import UnreservedTickets from './unreserved-tickets'
import UnreservedAreas from './unreserved-areas'
import { useTicketTypes } from '@lib/use-ticket-types'
import MealDeals from './meal-deals'
import BasketTotal from './basket-total'
import spektrixLogo from '@images/SpektrixLogo_700px.png'

const TicketType = ({ model, instance, mutate }) => {
  const ticketTypes = useTicketTypes(instance.spektrix_v3_id || instance.id, instance.commission_value, instance.hide_online_areas)
  const [mealDealSelection, setMealDealSelection] = useState(null)

  // Store meal deal selection in a ref to avoid render loops
  const mealDealSelectionRef = useRef(null)
  mealDealSelectionRef.current = mealDealSelection

  // Memoize the meal deals change callback to prevent infinite loops
  const handleMealDealsChange = useCallback((selection) => {
    // For reserved seating, store the selection for later
    if (model.reservedSeating) {
      setMealDealSelection(selection)
      // Also update the model immediately so readyToAddToBasket works
      model.mealDealSelection = selection
    }
    // For unreserved seating, meal deals are handled immediately in the MealDeals component
  }, [model])

  // Track if we've done initial assignment
  const initialAssignmentDone = useRef(false)
  
  // Auto-assign default ticket types for seats without one
  useEffect(() => {
    if (!initialAssignmentDone.current && ticketTypes.ticketOptions && ticketTypes.ticketOptions.length > 0 && model.seatsInBasket && model.seatsInBasket.length > 0) {
      // Track if we need to update seatActions
      let needsUpdate = false
      const newSeatActions = { ...model.seatActions }
      
      model.seatsInBasket.forEach(seat => {
        // Check if this seat already has a ticket type
        if (!seat.ticket?.type) {
          const seatId = String(seat.id)
          
          // Find available ticket types for this seat
          let availableOptions = ticketTypes.ticketOptions
          
          // Filter by seat availability if we have that data
          if (model.availableSeats && model.availableSeats[seatId]) {
            availableOptions = ticketTypes.ticketOptions.filter(
              o =>
                model.availableSeats[seatId].ok?.includes(o.band.id) ||
                model.availableSeats[seatId].basket?.includes(o.band.id)
            )
          }
          
          // Find a "Standard" ticket type or use the first available
          let defaultOption = availableOptions.find(o => 
            o.ticket?.ticketType?.name?.toLowerCase() === 'standard'
          )
          
          if (!defaultOption && availableOptions.length > 0) {
            defaultOption = availableOptions[0]
          }
          
          if (defaultOption) {
            needsUpdate = true
            // Set the default ticket type
            if (!newSeatActions[seatId]) {
              newSeatActions[seatId] = {}
            }
            newSeatActions[seatId].ticket = {
              type: { id: defaultOption.ticket.ticketType.id },
              band: defaultOption.band
            }
          }
        }
      })
      
      // Only update if we made changes
      if (needsUpdate) {
        model.seatActions = newSeatActions
        initialAssignmentDone.current = true
      }
    }
  }, [ticketTypes.ticketOptions?.length, model.seatsInBasket?.length])

  // Calculate main tickets count (excluding those marked for removal)
  const mainTicketsCount = model.reservedSeating ? 
    model.seatsInBasket.filter(s => {
      const seatId = String(s.id)
      // Check if seat is marked for removal
      if (model.seatActions[seatId]?.removeFromBasket) return false
      // Check if seat has a ticket type (either from basket or from seatActions)
      return s.ticket?.type || model.seatActions[seatId]?.ticket?.type
    }).length : 
    model.unreservedTicketsInBasket.length

  // Check if event is a class or workshop
  const isClassOrWorkshop = model.event?.category_list?.some(category => {
    const lowerCategory = category.toLowerCase()
    // Use word boundaries to match complete words only
    return /\bclass\b/.test(lowerCategory) || /\bworkshop\b/.test(lowerCategory)
  })

  // Update meal deal selection when main tickets change
  useEffect(() => {
    // If no main tickets, clear meal deal selection
    if (mainTicketsCount === 0 && mealDealSelection !== null) {
      setMealDealSelection(null)
      model.mealDealSelection = null
    } else if (mealDealSelection && mealDealSelection.count > mainTicketsCount) {
      // If we have more meal deals than tickets, update the count
      const updatedSelection = { ...mealDealSelection, count: mainTicketsCount }
      setMealDealSelection(updatedSelection)
      model.mealDealSelection = updatedSelection
    }
  }, [mainTicketsCount])

  // If all seats are removed and we're showing ticket types, go back to seat selection
  useEffect(() => {
    if (model.reservedSeating && model.showTicketTypes && model.seatsInBasket.length === 0) {
      model.showTicketTypes = false
    }
  }, [model.seatsInBasket.length, model.reservedSeating, model.showTicketTypes, model])

  return (
    <div className='flex flex-col gap-3 w-full px-4 sm:px-0 relative' style={{ overflow: 'visible' }}>
      {/* Loading overlay - full screen */}
      {model.addingToBasket && (
        <div className='fixed inset-0 bg-white/90 dark:bg-gray-900/90 z-[9999] flex items-center justify-center'>
          <div className='flex flex-col items-center space-y-3 bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl'>
            <ArrowPathIcon className='w-12 h-12 animate-spin text-blue-600 dark:text-blue-400' />
            <p className='text-gray-700 dark:text-gray-300 font-medium text-lg'>Updating basket...</p>
            <p className='text-gray-500 dark:text-gray-400 text-sm'>Please wait while we process your changes</p>
          </div>
        </div>
      )}
      
      <div className='flex flex-col gap-3' style={{ overflow: 'visible' }}>
        {/* Header with back button */}
        <div className='flex items-center gap-3'>
          {model.reservedSeating && (
            <button
              className={clsx(
                'rounded-full p-2',
                'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600',
                'text-gray-700 dark:text-gray-300'
              )}
              onClick={() => (model.showTicketTypes = false)}
              title="Back to seat selection"
            >
              <ChevronLeftIcon className='w-5 h-5' />
            </button>
          )}
          <div className='flex flex-col gap-1'>
            <h2 className='text-lg font-semibold text-gray-900 dark:text-white'>
              Select ticket types
              {model.plan?.type === 'Unreserved'
                ? ' and quantities'
                : ' for your selected seats'}
            </h2>
            {model.currentBasket.length > 0 && model.reservedSeating && (
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                You can change ticket types by clicking on the dropdowns below
              </p>
            )}
          </div>
        </div>
      </div>
      {model.seats.length > 0 ? (
        <ReservedTickets
          model={model}
          ticketOptions={ticketTypes.ticketOptions}
        />
      ) : model.groupUnreservedSeating ? (
        <UnreservedAreas
          instance={model.instance}
          plan={model.plan}
          unreservedTicketsInBasket={model.unreservedTicketsInBasket}
          mutate={mutate}
          hideOnlineAreas={instance.hide_online_areas}
        />
      ) : (
        <UnreservedTickets
          instance={model.instance}
          plan={model.plan}
          unreservedTicketsInBasket={model.unreservedTicketsInBasket}
          ticketOptions={ticketTypes.ticketOptions}
          mutate={mutate}
          instanceData={{
            items: model.unreservedTicketsInBasket,
            mealDeals: model.currentBasket?.filter(t => 
              t.event?.name?.toLowerCase().includes('dinner offer') ||
              t.event?.name?.toLowerCase().includes('meal deal')
            ) || []
          }}
        />
      )}
      
      {/* Meal Deals Section - show if instance has AVAILABLE meal deals AND there are tickets in basket AND not a class/workshop */}
      {(() => {
        if (!instance.meal_deals || instance.meal_deals.length === 0 || mainTicketsCount === 0 || isClassOrWorkshop) {
          return null
        }
        
        // Check if any meal deals are actually available
        const hasAvailableMealDeals = instance.meal_deals.some(md => {
          if (md.available <= 0) return false
          if (md.stop_selling_at) {
            const stopSellingTime = new Date(md.stop_selling_at)
            if (stopSellingTime <= new Date()) return false
          }
          return true
        })
        
        if (!hasAvailableMealDeals) {
          return null
        }
        
        return (
          <div className='border-t border-gray-200 dark:border-gray-700 pt-4 mt-4'>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3'>
              Add Meal Deals
            </h3>
            <MealDeals 
              instance={instance} 
              mainTicketsInBasket={model.reservedSeating ? 
                model.seatsInBasket.filter(s => {
                  const seatId = String(s.id)
                  // Check if seat is marked for removal
                  if (model.seatActions[seatId]?.removeFromBasket) return false
                  // Check if seat has a ticket type (either from basket or from seatActions)
                  return s.ticket?.type || model.seatActions[seatId]?.ticket?.type
                }) : 
                model.unreservedTicketsInBasket
              }
              showBeforeBasket={model.reservedSeating}
              onMealDealsChange={handleMealDealsChange}
              mutate={mutate}
              model={model}
            />
          </div>
        )
      })()}
      
      {/* Basket Total */}
      <div className='border-t border-gray-200 dark:border-gray-700 pt-4 mt-4'>
        <BasketTotal 
          instance={instance} 
          event={model.event}
          pendingMealDealSelection={mealDealSelection && mealDealSelection.count > 0 && !model.mealDealsToRemove ? mealDealSelection : null}
          pendingTickets={model.seatsInBasket}
          seatActions={model.seatActions}
          ticketOptions={ticketTypes.ticketOptions}
          mealDealsToRemove={model.mealDealsToRemove}
          commissionValue={instance.commission_value}
        />
      </div>
      
      {/* Add to Basket Button - only show for reserved seating or if there are changes other than meal deals */}
      {(model.reservedSeating || model.ticketsToRemove.length > 0 || model.ticketsToModify.length > 0) && (
        <div className='pt-4'>
          <button
            className={clsx(
              'w-full rounded-lg py-3 px-4',
              'bg-blue-600 text-white dark:bg-blue-500',
              'hover:bg-blue-700 dark:hover:bg-blue-600',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'font-semibold text-lg flex items-center justify-center gap-2'
            )}
            disabled={!model.readyToAddToBasket || model.addingToBasket}
            onClick={() => {
              model.mealDealSelection = mealDealSelectionRef.current
              model.addToBasket()
            }}
          >
            {model.addingToBasket ? (
              <>
                <ArrowPathIcon className='w-5 h-5 animate-spin' />
                <span>Adding to basket...</span>
              </>
            ) : (
              <>
                <PlusCircleIcon className='w-5 h-5' />
                <span>{model.currentBasket.length > 0 ? 'Update' : 'Add to'} basket</span>
              </>
            )}
          </button>
        </div>
      )}
      
      {/* View Basket Button - show for unreserved seating when tickets are in basket */}
      {!model.reservedSeating && model.unreservedTicketsInBasket.length > 0 && (
        <div className='pt-4'>
          <a
            href='/basket'
            className={clsx(
              'w-full rounded-lg py-3 px-4',
              'bg-blue-600 text-white dark:bg-blue-500',
              'hover:bg-blue-700 dark:hover:bg-blue-600',
              'font-semibold text-lg flex items-center justify-center gap-2',
              'inline-block text-center'
            )}
          >
            View basket
          </a>
        </div>
      )}
      
      {/* Spektrix Logo */}
      <div className='mt-4 pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-center'>
        <div className='flex items-center gap-1'>
          <p className='text-[10px] text-gray-400 dark:text-gray-500'>Powered by</p>
          <div className='bg-gray-100 dark:bg-gray-200 rounded px-1.5 py-0.5 inline-block'>
            <img 
              src={spektrixLogo} 
              alt='Spektrix' 
              className='h-3'
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default TicketType